import React, { useCallback, useState, useEffect } from "react";
import styles from "../../Style/Components/input.module.scss";
import { useDropzone } from "react-dropzone";
import Icon from "../icons";
import { PrimaryButton } from "../Cta";
import { APIService } from "../../Service"; // keep your existing APIService
import { toast } from "react-toastify";

// This component keeps backwards compatibility with your existing handleChange(file)
// and adds a new prop `onUploadComplete(viewUrl)` which will be called with the
// public URL returned by your backend after successful upload.

export default function CustomFileUpload(props) {
  const {
    handleChange,
    onUploadComplete,
    uploadOnSelect = true,
    fileSize = 3, // default MB
    type,
    label,
    require,
    handleClear,
    file: externalFile,
  } = props;

  const [localFile, setLocalFile] = useState(null); // File object for preview
  const [isUploading, setIsUploading] = useState(false);

  // If the parent controlled `props.file` changes (for example after it stores the URL),
  // keep local preview in sync.
  useEffect(() => {
    setLocalFile(
      externalFile instanceof File || externalFile instanceof Blob
        ? externalFile
        : null
    );
  }, [externalFile]);

  const uploadFile = useCallback(
    async (file) => {
      if (!file) return null;
      setIsUploading(true);
      try {
        // 1. request presigned url / upload info from your API
        const is_post = true;
        const url = "general/upload"; // same as your snippet
        const data = {
          type: "ADMIN",
          file: file.name,
          is_public: true,
        };

        const response = await APIService(is_post, url, data);
        if (!response || response.status_code !== 200) {
          toast.error(response?.message || "Failed to get upload URL", {
            className: "e-toast",
          });
          setIsUploading(false);
          return null;
        }

        const aws_fields = response.data.upload_info.fields || {};
        const aws_url = response.data.upload_info.url;

        // 2. upload to S3 (form POST)
        const formData = new FormData();
        Object.entries(aws_fields).forEach(([k, v]) => formData.append(k, v));
        // append file under the key expected by S3 POST (usually 'file')
        formData.append("file", file, file.name);

        const s3Res = await fetch(aws_url, {
          method: "POST",
          body: formData,
        });

        if (!s3Res.ok) {
          toast.error("Upload to S3 failed", { className: "e-toast" });
          setIsUploading(false);
          return null;
        }

        // 3. final public view URL is provided by your original API response
        const viewUrl = response.data.view_info;
        setIsUploading(false);

        // inform parent about the final URL
        if (typeof onUploadComplete === "function") onUploadComplete(viewUrl);

        return viewUrl;
      } catch (err) {
        console.error(err);
        toast.error("An error occurred while uploading the file.", {
          className: "e-toast",
        });
        setIsUploading(false);
        return null;
      }
    },
    [onUploadComplete]
  );

  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (acceptedFiles && acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        if (typeof handleChange === "function")
          // keep original behaviour: tell parent about the selected File object
          handleChange(file);

        // show local preview immediately
        setLocalFile(file);

        if (uploadOnSelect) {
          await uploadFile(file);
        }
      }
    },
    [handleChange, uploadOnSelect, uploadFile]
  );

  const onDropRejected = useCallback(() => {
    toast.error(`Image must be below ${fileSize}MB`, { className: "e-toast" });
  }, [fileSize]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    maxSize: fileSize * 1024 * 1024,
    maxFiles: 1,
    multiple: false,
    accept: {
      "image/*": [".png", ".jpeg", ".webp", "jpg"],
    },
  });

  // UI: show preview either from localFile (File/Blob) or externalFile (string URL)
  const previewSrc = localFile
    ? URL.createObjectURL(localFile)
    : typeof externalFile === "string"
    ? externalFile
    : "";

  return (
    <div>
      {externalFile || localFile ? (
        <div
          className={` ${
            props.error
              ? styles.e_uploaded_wrapper_error
              : styles.e_uploaded_wrapper
          }`}
        >
          {type === 2 ? (
            <div className="row d-flex flex-column">
              <div className="d-flex w-100 position-relative">
                <img
                  src={previewSrc}
                  alt="preview"
                  className="img-fluid"
                  style={{ height: "50px" }}
                />
                <div className=" position-absolute end-0 top-0-8px e-border-radius-100px e-bg-pale-ruby py-0 px-2 e-h-fit">
                  <PrimaryButton
                    icon="delete"
                    size="12px"
                    ClassName="bg-transparent p-0 "
                    onPress={handleClear}
                  />
                </div>
              </div>

              <div className="d-flex flex-column justify-content-between align-items-start row-gap-1 px-2">
                {localFile && <p className="...">{localFile.name}</p>}
                {localFile && (
                  <p className="...">
                    {(localFile.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                )}
              </div>

              <div {...getRootProps()}>
                <input {...getInputProps()} />
                <PrimaryButton
                  onPress={() => {}}
                  ClassName="e-border-radius-pill py-2 e-border-1px-blue e-font-weight-500 e-font-14 w-100 bg-transparent e-text-blue-violet e-primary-font-poppins"
                  name={isUploading ? "Uploading..." : "Choose file"}
                />
              </div>
            </div>
          ) : (
            <div className="row">
              <div className="col-lg-6">
                <div className="row row-cols-2">
                  <div className="col">
                    <img src={previewSrc} alt="preview" className="img-fluid" />
                  </div>
                  <div className="col d-flex flex-column justify-content-between align-items-start row-gap-2 px-2">
                    <PrimaryButton
                      icon="delete"
                      ClassName="bg-transparent p-0 "
                      onPress={handleClear}
                    />
                    {localFile && <p className="...">{localFile.name}</p>}
                    {localFile && (
                      <p className="...">
                        {(localFile.size / (1024 * 1024)).toFixed(2)} MB
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <div className="offset-3 col-3 d-flex align-items-center">
                <div {...getRootProps()}>
                  <input {...getInputProps()} />
                  <PrimaryButton
                    onPress={() => {}}
                    ClassName="e-border-radius-pill py-2 e-border-1px-blue e-font-weight-500 e-font-14 w-100 bg-transparent e-text-blue-violet e-primary-font-poppins"
                    name={"Choose file"}
                    loading={isUploading}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div {...getRootProps()}>
          <input {...getInputProps()} />
          {type === 2 ? (
            <div
              className={`${
                props.error
                  ? styles.e_uploaded_wrapper_error
                  : "e-border-1px-blue"
              } e-border-radius-16 p-4 e-cursor-pointer`}
            >
              <label className="e-text-black e-primary-font-poppins  e-font-weight-400  e-font-14 pb-2">
                {label}
                {require && <span className="text-danger">*</span>}
              </label>
              {isDragActive ? (
                <h5 className="m-0 e-font-16 e-font-weight-400 e-text-purple e-primary-font-poppins text-center">
                  Drop Your File Here
                </h5>
              ) : (
                <div className="d-flex w-100 h-100 flex-column align-items-center justify-content-center row-gap-2">
                  <Icon icon="upload" size="34px" />
                  <p className="m-0 e-font-14 e-font-weight-400 e-text-purple e-primary-font-poppins text-center">
                    Choose file
                    <span className=" e-text-gray">or drop here</span>
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div
              className={
                props.error
                  ? styles.e_uploaded_wrapper_error
                  : styles.e_uploaded_wrapper
              }
            >
              <label className="e-text-black e-primary-font-poppins  e-font-weight-400  e-font-14 pb-2">
                {label}
                {require && <span className="text-danger">*</span>}
              </label>
              {isDragActive ? (
                <h5 className="m-0 e-font-16 e-font-weight-400 e-text-purple e-primary-font-poppins">
                  Drop Your File Here
                </h5>
              ) : (
                <div className="d-flex w-100 h-100 flex-column align-items-center justify-content-center row-gap-2">
                  <Icon icon="upload" size="34px" />
                  <p className="m-0 e-font-14 e-font-weight-400 e-text-purple e-primary-font-poppins">
                    Choose file{" "}
                    <span className=" e-text-gray">or drop here</span>
                  </p>
                </div>
              )}
            </div>
          )}

          {type === 2 ? (
            <div className="d-flex flex-column align-items-start pt-2 ps-2">
              <p className="m-0 e-font-12 e-font-weight-400 e-primary-font-poppins">
                Files supported: PNG
              </p>
              <p className="m-0 e-font-12 e-font-weight-400 e-primary-font-poppins">
                Maximum size: {fileSize} MB
              </p>
            </div>
          ) : (
            <div className="d-flex justify-content-between align-items-center pt-2">
              <p className="m-0 e-font-12 e-font-weight-400 e-primary-font-poppins">
                Files supported: PNG
              </p>
              <p className="m-0 e-font-12 e-font-weight-400 e-primary-font-poppins">
                Maximum size: {fileSize} MB
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
